import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertTriangle,
  Bot,
  Brain,
  CheckCircle2,
  Clock,
  FileSearch,
  Gavel,
  LayoutList,
  Loader2,
  RefreshCw,
  Scale,
  ShieldCheck,
  Sparkle,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { AgentAnalysis } from "@/services/opportunities.service";

const ICONS: Record<string, typeof Bot> = {
  Target,
  Brain,
  Scale,
  Gavel,
  Users,
  TrendingUp,
  ShieldCheck,
  FileSearch,
  AlertTriangle,
};

type EstadoAgente = "analizando" | "error" | "listo" | "pendiente" | "desactivado";

function estadoDe(a: AgentAnalysis, loading: boolean, error?: string): EstadoAgente {
  if (loading) return "analizando";
  if (error) return "error";
  if (a.contenido) return "listo";
  return a.activo ? "pendiente" : "desactivado";
}

const ESTADO_META: Record<EstadoAgente, { label: string; dot: string; text: string }> = {
  analizando: { label: "Analizando", dot: "bg-primary animate-pulse", text: "text-primary" },
  error: { label: "Error", dot: "bg-destructive", text: "text-destructive" },
  listo: { label: "Listo", dot: "bg-success", text: "text-success" },
  pendiente: { label: "Pendiente", dot: "bg-muted-foreground/50", text: "text-muted-foreground" },
  desactivado: { label: "Desactivado", dot: "bg-border", text: "text-muted-foreground" },
};

/** Render Markdown con la tipografía del sistema (sin plugin de prose). */
function Markdown({ children }: { children: string }) {
  return (
    <div className="text-sm leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (p) => <h4 className="font-display text-base font-semibold mt-4 mb-2" {...p} />,
          h2: (p) => (
            <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-primary mt-5 mb-2" {...p} />
          ),
          h3: (p) => <h5 className="font-semibold mt-4 mb-1.5" {...p} />,
          p: (p) => <p className="mb-3" {...p} />,
          ul: (p) => <ul className="mb-3 space-y-1.5 list-disc pl-5 marker:text-primary" {...p} />,
          ol: (p) => <ol className="mb-3 space-y-1.5 list-decimal pl-5" {...p} />,
          li: (p) => <li className="pl-0.5" {...p} />,
          strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
          code: (p) => <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs" {...p} />,
          table: (p) => (
            <div className="mb-3 overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs" {...p} />
            </div>
          ),
          th: (p) => <th className="bg-muted/60 px-2 py-1.5 text-left font-semibold" {...p} />,
          td: (p) => <td className="border-t border-border px-2 py-1.5 align-top" {...p} />,
          blockquote: (p) => (
            <blockquote className="mb-3 border-l-2 border-primary/40 pl-3 text-muted-foreground" {...p} />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

export interface AgentConsoleProps {
  agentes: AgentAnalysis[];
  enCurso: string[];
  errores: Record<string, string>;
  cargando?: boolean;
  /** Error real de la carga de agentes (para no mostrar un falso "no hay agentes"). */
  errorCarga?: string | null;
  onReintentar?: () => void;
  onRun: (agenteId: string) => void;
  onRunAll: () => void;
}

/**
 * Consola de agentes IA en dos paneles: navegación por agente a la izquierda y
 * el análisis completo a la derecha. La pestaña "Comparar" resume a todos.
 */
export function AgentConsole({
  agentes,
  enCurso,
  errores,
  cargando,
  errorCarga,
  onReintentar,
  onRun,
  onRunAll,
}: AgentConsoleProps) {
  const [tab, setTab] = useState<"consola" | "comparar">("consola");
  const [seleccionado, setSeleccionado] = useState<string | null>(null);

  // Selección por defecto: el primer agente con análisis, o el primero activo.
  useEffect(() => {
    if (!agentes.length) return;
    if (seleccionado && agentes.some((a) => a.agenteId === seleccionado)) return;
    const conAnalisis = agentes.find((a) => a.contenido);
    setSeleccionado((conAnalisis ?? agentes.find((a) => a.activo) ?? agentes[0]).agenteId);
  }, [agentes, seleccionado]);

  const actual = useMemo(
    () => agentes.find((a) => a.agenteId === seleccionado) ?? null,
    [agentes, seleccionado],
  );

  const listos = agentes.filter((a) => a.contenido).length;
  const activos = agentes.filter((a) => a.activo).length;

  if (cargando) {
    return (
      <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
        <div className="h-6 w-52 rounded bg-muted animate-pulse" />
        <div className="mt-4 grid gap-4 md:grid-cols-[220px_1fr]">
          <div className="h-64 rounded-lg bg-muted animate-pulse" />
          <div className="h-64 rounded-lg bg-muted animate-pulse" />
        </div>
      </section>
    );
  }

  if (errorCarga) {
    const sinMotor = errorCarga.includes("no está configurado");
    return (
      <section className="rounded-xl border border-destructive/40 bg-destructive/5 p-5 shadow-elegant">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="size-4 text-destructive" />
          {sinMotor ? "Motor IA no configurado" : "No se pudieron cargar los agentes"}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">{errorCarga}</p>
        <div className="mt-4 flex gap-2">
          {onReintentar && (
            <button
              onClick={onReintentar}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted"
            >
              <RefreshCw className="size-3.5" /> Reintentar
            </button>
          )}
          <a
            href="/ia"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted"
          >
            <Bot className="size-3.5" /> Ir a Motor IA
          </a>
        </div>
      </section>
    );
  }

  if (!agentes.length) {
    return (
      <section className="rounded-xl border border-border bg-card p-5 shadow-elegant">
        <p className="text-sm text-muted-foreground">
          No hay agentes disponibles. Actívalos en Motor IA.
        </p>
      </section>
    );
  }


  return (
    <section className="rounded-xl border border-border bg-card shadow-elegant overflow-hidden">
      {/* Barra superior */}
      <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <h3 className="font-display font-semibold flex items-center gap-2 text-sm">
          <Bot className="size-4 text-primary" /> Consola de agentes IA
        </h3>
        <span className="text-[11px] text-muted-foreground">
          {listos}/{activos} análisis generados
        </span>

        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-md border border-border p-0.5">
            {(
              [
                { id: "consola", label: "Consola", icon: LayoutList },
                { id: "comparar", label: "Comparar", icon: Sparkle },
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition",
                  tab === t.id
                    ? "bg-muted font-medium text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <t.icon className="size-3.5" /> {t.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onRunAll}
            disabled={enCurso.length > 0 || activos === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs transition hover:bg-muted disabled:opacity-50"
          >
            {enCurso.length > 0 ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Analizar todo
          </button>
        </div>
      </header>

      {tab === "comparar" ? (
        <ComparativaAgentes agentes={agentes} enCurso={enCurso} errores={errores} onRun={onRun} />
      ) : (
        <div className="flex flex-col md:flex-row">
          {/* Rail de agentes */}
          <nav
            aria-label="Agentes disponibles"
            className="flex shrink-0 gap-2 overflow-x-auto border-b border-border p-2 md:w-64 md:max-h-[42rem] md:flex-col md:overflow-y-auto md:border-b-0 md:border-r"
          >

            {agentes.map((a) => {
              const estado = estadoDe(a, enCurso.includes(a.agenteId), errores[a.agenteId]);
              const meta = ESTADO_META[estado];
              const Icon = ICONS[a.icono] ?? Bot;
              const activoUi = a.agenteId === seleccionado;
              return (
                <button
                  key={a.agenteId}
                  type="button"
                  onClick={() => setSeleccionado(a.agenteId)}
                  aria-current={activoUi}
                  className={cn(
                    "flex min-w-[190px] items-start gap-2.5 rounded-lg border p-2.5 text-left transition md:min-w-0",
                    activoUi
                      ? "border-primary/40 bg-primary/5"
                      : "border-transparent hover:bg-muted/60",
                  )}
                >
                  <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="size-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-foreground">
                      {a.nombre}
                    </span>
                    <span className={cn("mt-1 flex items-center gap-1.5 text-[10px]", meta.text)}>
                      <span className={cn("size-1.5 rounded-full", meta.dot)} />
                      {meta.label}
                      {a.origenEjecucion === "auto" && estado === "listo" && (
                        <span className="inline-flex items-center gap-0.5 text-primary">
                          <Zap className="size-2.5" /> auto
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Panel de análisis */}
          <div className="min-w-0 flex-1 p-4 md:max-h-[42rem] md:overflow-y-auto">
            {actual && (
              <DetalleAgente
                agente={actual}
                loading={enCurso.includes(actual.agenteId)}
                error={errores[actual.agenteId] || undefined}
                onRun={() => onRun(actual.agenteId)}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function DetalleAgente({
  agente,
  loading,
  error,
  onRun,
}: {
  agente: AgentAnalysis;
  loading: boolean;
  error?: string;
  onRun: () => void;
}) {
  const Icon = ICONS[agente.icono] ?? Bot;
  return (
    <article>
      <div className="flex flex-wrap items-start gap-3 border-b border-border pb-3">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="font-display text-sm font-semibold text-foreground">{agente.nombre}</h4>
          <p className="text-xs text-muted-foreground">{agente.descripcion}</p>
          {agente.creadoAt && (
            <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <Clock className="size-3" />
              {new Date(agente.creadoAt).toLocaleString("es-CL")}
              {agente.modelo && <span>· {agente.modelo}</span>}
              {agente.origenEjecucion === "auto" && (
                <span className="inline-flex items-center gap-0.5 text-primary">
                  <Zap className="size-3" /> piloto automático
                </span>
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRun}
          disabled={loading || !agente.activo}
          title={agente.activo ? undefined : "Actívalo en Motor IA"}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow-glow transition hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          {agente.contenido ? "Re-analizar" : "Analizar"}
        </button>
      </div>

      {error && (
        <p className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          {error}
        </p>
      )}

      {loading && (
        <div className="mt-4 space-y-2">
          <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
          <div className="h-4 w-full rounded bg-muted animate-pulse" />
          <div className="h-4 w-5/6 rounded bg-muted animate-pulse" />
        </div>
      )}

      {!loading && agente.resumen && (
        <p className="mt-3 flex gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-foreground">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
          {agente.resumen}
        </p>
      )}

      {!loading && agente.contenido && (
        <div className="mt-4">
          <Markdown>{agente.contenido}</Markdown>
        </div>
      )}

      {!loading && !agente.contenido && !error && (
        <p className="mt-4 text-sm text-muted-foreground">
          {agente.activo
            ? "Este agente aún no ha analizado la licitación. Ejecútalo para obtener hallazgos, riesgos y acciones recomendadas."
            : "Agente desactivado para tu empresa. Actívalo en Motor IA para usarlo."}
        </p>
      )}
    </article>
  );
}

function ComparativaAgentes({
  agentes,
  enCurso,
  errores,
  onRun,
}: {
  agentes: AgentAnalysis[];
  enCurso: string[];
  errores: Record<string, string>;
  onRun: (agenteId: string) => void;
}) {
  return (
    <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
      {agentes.map((a) => {
        const estado = estadoDe(a, enCurso.includes(a.agenteId), errores[a.agenteId]);
        const meta = ESTADO_META[estado];
        const Icon = ICONS[a.icono] ?? Bot;
        return (
          <article key={a.agenteId} className="rounded-lg border border-border p-3">
            <div className="flex items-start gap-2.5">
              <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Icon className="size-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-xs font-semibold text-foreground">{a.nombre}</h4>
                <span className={cn("mt-0.5 flex items-center gap-1.5 text-[10px]", meta.text)}>
                  <span className={cn("size-1.5 rounded-full", meta.dot)} /> {meta.label}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRun(a.agenteId)}
                disabled={enCurso.includes(a.agenteId) || !a.activo}
                className="rounded-md border border-border px-2 py-1 text-[11px] transition hover:bg-muted disabled:opacity-50"
              >
                {a.contenido ? "Re-analizar" : "Analizar"}
              </button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {errores[a.agenteId] ||
                a.resumen ||
                (a.activo ? "Sin análisis todavía." : "Agente desactivado.")}
            </p>
          </article>
        );
      })}
    </div>
  );
}
